import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../auth/AuthProvider";

const ChallengeDashboard = () => {
  const { user } = useAuth();
  const { challengeId } = useParams();
  const navigate = useNavigate();

  const [challenge, setChallenge] = useState(null);
  const [progressData, setProgressData] = useState({});
  const [weekData, setWeekData] = useState({});
  const [weekDates, setWeekDates] = useState([]);
  const [stats, setStats] = useState({
    totalThisYear: 0,
    currentStreak: 0,
    longestStreak: 0,
    bestDay: { date: null, count: 0 },
    averagePerDay: 0,
    daysLogged: 0,
  });
  const [loading, setLoading] = useState(true);

  const getWeekDates = () => {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = currentDay === 0 ? -6 : 1 - currentDay;
    const dates = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + mondayOffset + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const calculateStats = (progress) => {
    const currentYear = new Date().getFullYear();
    const yearStart = `${currentYear}-01-01`;
    const today = new Date().toISOString().split('T')[0];

    // Filter to current year and sort dates
    const entries = Object.entries(progress)
      .filter(([date]) => date >= yearStart)
      .sort(([a], [b]) => a.localeCompare(b));

    let totalThisYear = 0;
    let bestDay = { date: null, count: 0 };
    let daysLogged = 0;

    entries.forEach(([date, data]) => {
      totalThisYear += data.total;
      daysLogged++;
      if (data.total > bestDay.count) {
        bestDay = { date, count: data.total };
      }
    });

    // Calculate streaks
    let currentStreak = 0;
    let longestStreak = 0;
    let tempStreak = 0;

    // Get all dates from year start to today
    const allDates = [];
    const startDate = new Date(yearStart);
    const endDate = new Date(today);
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      allDates.push(d.toISOString().split('T')[0]);
    }

    // Calculate streaks
    allDates.forEach((date, index) => {
      const hasEntry = progress[date] && progress[date].total > 0;

      if (hasEntry) {
        tempStreak++;
        if (tempStreak > longestStreak) {
          longestStreak = tempStreak;
        }
      } else {
        tempStreak = 0;
      }
    });

    // Calculate current streak (from today backwards)
    for (let i = allDates.length - 1; i >= 0; i--) {
      const date = allDates[i];
      const hasEntry = progress[date] && progress[date].total > 0;

      // Skip today if no entry yet (still time to log)
      if (i === allDates.length - 1 && !hasEntry) {
        continue;
      }

      if (hasEntry) {
        currentStreak++;
      } else {
        break;
      }
    }

    const totalDaysElapsed = allDates.length;
    const averagePerDay = totalDaysElapsed > 0 ? Math.round(totalThisYear / totalDaysElapsed) : 0;

    return {
      totalThisYear,
      currentStreak,
      longestStreak,
      bestDay,
      averagePerDay,
      daysLogged,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !challengeId) return;
      setLoading(true);

      try {
        // Fetch challenge details
        const challengeRef = doc(db, `users/${user.uid}/challenges/${challengeId}`);
        const challengeSnap = await getDoc(challengeRef);

        if (challengeSnap.exists()) {
          setChallenge({ id: challengeSnap.id, ...challengeSnap.data() });
        }

        // Fetch all progress for this challenge
        const progressRef = collection(db, `users/${user.uid}/challenges/${challengeId}/progress`);
        const progressSnap = await getDocs(progressRef);

        const progress = {};
        progressSnap.docs.forEach((doc) => {
          progress[doc.id] = doc.data();
        });
        setProgressData(progress);

        // Calculate week data
        const dates = getWeekDates();
        setWeekDates(dates);

        const week = {};
        dates.forEach((date, index) => {
          if (progress[date]) {
            week[index] = { total: progress[date].total };
          }
        });
        setWeekData(week);

        // Calculate stats
        setStats(calculateStats(progress));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, challengeId]);

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const todayIndex = (new Date().getDay() + 6) % 7;
  const unit = challenge?.goalType === 'minutes' ? 'min' : '';

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-6" />
          <div className="h-40 bg-gray-200 dark:bg-gray-700 rounded-xl mb-6" />
          <div className="grid grid-cols-2 gap-4">
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
            <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="max-w-2xl mx-auto px-2 sm:px-6 lg:px-8">
        <p className="text-gray-600 dark:text-gray-400">Challenge not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-2 sm:px-6 lg:px-8">
      {/* Back button */}
      <button
        onClick={() => navigate("/")}
        className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-6"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back
      </button>

      {/* Challenge Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {challenge.originalName}
        </h1>
        {challenge.description && (
          <p className="text-gray-600 dark:text-gray-400 mt-1">{challenge.description}</p>
        )}
      </div>

      {/* Weekly Tracker */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-4 sm:p-6 mb-6 text-white">
        <h3 className="text-sm font-medium text-white/80 mb-4">This Week</h3>
        <div className="flex justify-between items-end gap-1 sm:gap-2">
          {days.map((day, index) => {
            const dayData = weekData[index] || { total: 0 };
            const isToday = index === todayIndex;
            const hasProgress = dayData.total > 0;
            const percentage = challenge.goal > 0 ? Math.min((dayData.total / challenge.goal) * 100, 100) : 0;

            return (
              <button
                key={day}
                onClick={() => navigate(`/register?challenge=${challengeId}&date=${weekDates[index]}`)}
                className="flex flex-col items-center gap-2 group"
              >
                <span className={`text-xs font-medium ${isToday ? 'text-white' : 'text-white/60'}`}>
                  {day}
                </span>
                <div className={`relative w-8 h-8 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-all group-hover:scale-110 ${
                  isToday
                    ? 'bg-white/30 ring-2 ring-white ring-offset-1 sm:ring-offset-2 ring-offset-indigo-500'
                    : hasProgress
                      ? 'bg-white/20'
                      : 'bg-white/10'
                }`}>
                  <svg className="absolute w-8 h-8 sm:w-12 sm:h-12 -rotate-90" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="20" stroke="currentColor" strokeWidth="4" fill="none" className="text-white/20" />
                    {hasProgress && (
                      <circle
                        cx="24" cy="24" r="20"
                        stroke="currentColor" strokeWidth="4" fill="none"
                        strokeDasharray={`${percentage * 1.256} 125.6`}
                        className="text-white"
                      />
                    )}
                  </svg>
                  {hasProgress ? (
                    <svg className="w-3 h-3 sm:w-5 sm:h-5 text-white z-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <span className="text-white/40 text-[10px] sm:text-sm z-10">-</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {/* Current Streak */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🔥</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Current Streak</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.currentStreak} <span className="text-lg font-normal text-gray-500 dark:text-gray-400">days</span>
          </p>
        </div>

        {/* Longest Streak */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🏆</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Longest Streak</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.longestStreak} <span className="text-lg font-normal text-gray-500 dark:text-gray-400">days</span>
          </p>
        </div>

        {/* Total This Year */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📊</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Total {new Date().getFullYear()}</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.totalThisYear.toLocaleString()} <span className="text-lg font-normal text-gray-500 dark:text-gray-400">{unit}</span>
          </p>
        </div>

        {/* Average Per Day */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📈</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Avg Per Day</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.averagePerDay} <span className="text-lg font-normal text-gray-500 dark:text-gray-400">{unit}</span>
          </p>
        </div>

        {/* Best Day */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">⭐</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Best Day</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.bestDay.count} <span className="text-lg font-normal text-gray-500 dark:text-gray-400">{unit}</span>
          </p>
          {stats.bestDay.date && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {new Date(stats.bestDay.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </p>
          )}
        </div>

        {/* Days Logged */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-md">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📅</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">Days Logged</span>
          </div>
          <p className="text-3xl font-bold text-gray-900 dark:text-white">
            {stats.daysLogged} <span className="text-lg font-normal text-gray-500 dark:text-gray-400">days</span>
          </p>
        </div>
      </div>

      {/* Add Entry Button */}
      <button
        onClick={() => navigate(`/register?challenge=${challengeId}`)}
        className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium transition-colors"
      >
        Add Entry
      </button>
    </div>
  );
};

export default ChallengeDashboard;
